/**
 * Masaüstü kabuğu — pencere yöneticisi ile.
 *
 * Yapı:
 *   ThemeBackdrop (App içinde)
 *   ├─ DesktopItems      → masaüstü kısayolları (sürüklenebilir)
 *   ├─ ManagedWindow[]   → sürüklenebilir pencereler
 *   ├─ Taskbar           → açık pencereler, kullanıcı, saat, kapat
 *   └─ StartMenu         → başlat menüsü
 */

import { useEffect, useState } from "react";

import type { StatusResponse } from "../lib/api";
import type { LoginSession } from "../login";
import { useSettings } from "../settings";
import {
  AboutWindow,
  DatabaseWindow,
  FilesWindow,
  OverviewWindow,
  ResourcesWindow,
  ScriptsWindow,
  TerminalWindow,
  ToolsWindow,
  NotesWindow,
  BrowserWindow,
  GamesWindow,
  UsersWindow,
  BackupsWindow,
} from "../apps";
import { DesktopItems } from "./DesktopItems";
import { SettingsWindow } from "./SettingsWindow";
import { ShutdownButton } from "./ShutdownButton";
import { StartMenu } from "./StartMenu";
import { StatusWindow } from "./StatusWindow";
import { StickyLayer, useStickyStore } from "../sticky";
import { useAccessStore } from "../lib/accessStore";
import { WidgetLayer } from "../widgets/WidgetLayer";
import { configureBridge } from "../lib/openExternal";
import { ManagedWindow } from "./window/ManagedWindow";
import { APP_DEFINITIONS, useWindowManager, type WindowApp } from "./window/windowStore";
import "./Desktop.css";

interface DesktopProps {
  status: StatusResponse | null;
  error: string | null;
  loading: boolean;
  session: LoginSession | null;
  reportSaved: boolean | null;
  onRefresh: () => void;
  onLock: () => void;
  onLogout: () => void;
}

export function Desktop({
  status,
  error,
  loading,
  session,
  reportSaved,
  onRefresh,
  onLock,
  onLogout,
}: DesktopProps) {
  const { settings } = useSettings();
  const [startOpen, setStartOpen] = useState(false);

  const windows = useWindowManager((state) => state.windows);
  const open = useWindowManager((state) => state.open);
  const focus = useWindowManager((state) => state.focus);
  const can = useAccessStore((state) => state.can);

  // Yapışkan notlar — görev çubuğundaki düğme yeni not açar
  const addNote = useStickyStore((state) => state.add);

  // Harici bağlantılar için köprü adresini ayarlardan bildir
  useEffect(() => {
    configureBridge(settings.bridge.url, settings.bridge.token);
  }, [settings.bridge.url, settings.bridge.token]);

  // Açılışta Genel Bakış penceresi (veya ?window=<ad> ile belirtilen)
  useEffect(() => {
    const requested = new URLSearchParams(window.location.search).get("window");
    // Geçerli liste `APP_DEFINITIONS`'tan türetilir — elle tutulan liste kaymaz.
    const valid = Object.keys(APP_DEFINITIONS) as WindowApp[];
    open(requested && valid.includes(requested as WindowApp) ? (requested as WindowApp) : "overview");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ctrl+K → başlat menüsü (Command Palette yerine hızlı erişim)
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLocaleLowerCase("tr") === "k") {
        event.preventDefault();
        setStartOpen((value) => !value);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  function renderApp(app: WindowApp) {
    // Erişim denetimi — izin yoksa içerik yerine uyarı gösterilir
    if (!can(app)) {
      return (
        <div className="desktop__placeholder">
          <span aria-hidden="true">🔒</span>
          <strong>Bu bölüme erişimin yok</strong>
          <p>Yöneticiden yetki istemen gerekiyor.</p>
        </div>
      );
    }

    switch (app) {
      case "overview":
        return <OverviewWindow />;
      case "scripts":
        return <ScriptsWindow />;
      case "terminal":
        return <TerminalWindow />;
      case "files":
        return <FilesWindow />;
      case "users":
        return <UsersWindow />;
      case "database":
        return <DatabaseWindow />;
      case "resources":
        return <ResourcesWindow />;
      case "tools":
        return <ToolsWindow />;
      case "notes":
        return <NotesWindow />;
      case "backups":
        return <BackupsWindow />;
      case "browser":
        return <BrowserWindow />;
      case "games":
        return <GamesWindow />;
      case "status":
        return (
          <StatusWindow
            status={status}
            error={error}
            loading={loading}
            session={session}
            reportSaved={reportSaved}
            onRefresh={onRefresh}
          />
        );
      case "settings":
        return <SettingsWindow />;
      case "about":
        return <AboutWindow />;
      default:
        return (
          <div className="desktop__placeholder">
            <span aria-hidden="true">🚧</span>
            <strong>Bu pencere henüz hazır değil</strong>
          </div>
        );
    }
  }

  return (
    <div className="desktop">
      {/* Masaüstü öğeleri — sürüklenebilir, sağ tık menülü */}
      <DesktopItems />

      {/* Masaüstü yapışkan notları — boş alana çift tıkla → yeni not */}
      <StickyLayer />

      {/* Donanım widget kartları (CPU/RAM/Disk/Sıcaklık) — + ile eklenir */}
      <WidgetLayer />

      {/* Pencereler */}
      {windows.map((win) => (
        <ManagedWindow key={win.id} window={win}>
          {renderApp(win.app)}
        </ManagedWindow>
      ))}

      {/* Başlat menüsü */}
      {startOpen && <StartMenu onClose={() => setStartOpen(false)} />}

      <Taskbar
        theme={settings.appearance.theme}
        session={session}
        onLock={onLock}
        onLogout={onLogout}
        onToggleStart={() => setStartOpen((value) => !value)}
        startOpen={startOpen}
        onToggleWindow={(id) => focus(id)}
        onNewNote={() => addNote()}
        ownerName={settings.general.ownerName}
        productName={settings.general.productName}
      />
    </div>
  );
}

// ----------------------------------------------------------------------
//  Görev çubuğu
// ----------------------------------------------------------------------
function Taskbar({
  theme,
  session,
  onLock,
  onLogout,
  onToggleStart,
  startOpen,
  onToggleWindow,
  onNewNote,
  ownerName,
  productName,
}: {
  theme: string;
  session: LoginSession | null;
  onLock: () => void;
  onLogout: () => void;
  onToggleStart: () => void;
  startOpen: boolean;
  onToggleWindow: (id: string) => void;
  onNewNote: () => void;
  /** Görev çubuğunda görünen sahip adı */
  ownerName: string;
  /** Ürün/şirket rozeti */
  productName: string;
}) {
  const [now, setNow] = useState(() => new Date());
  const windows = useWindowManager((state) => state.windows);
  const activeId = useWindowManager((state) => state.activeId);
  const toggleMinimize = useWindowManager((state) => state.toggleMinimize);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <footer className="taskbar">
      <button
        type="button"
        className={`taskbar__start${startOpen ? " is-active" : ""}`}
        onClick={onToggleStart}
        title="Başlat (Ctrl+K)"
      >
        <span className="taskbar__logo" aria-hidden="true">
          <i />
          <i />
          <i />
          <i />
        </span>
        Başlat
      </button>

      <div className="taskbar__apps">
        {windows.map((win) => (
          <button
            key={win.id}
            type="button"
            className={`taskbar__app${activeId === win.id ? " is-active" : ""}${
              win.minimized ? " is-minimized" : ""
            }`}
            onClick={() => {
              if (win.minimized) {
                onToggleWindow(win.id);
              } else if (activeId === win.id) {
                toggleMinimize(win.id);
              } else {
                onToggleWindow(win.id);
              }
            }}
            title={win.title}
          >
            <span aria-hidden="true">{win.icon}</span>
            <span className="taskbar__app-label">{win.title}</span>
          </button>
        ))}
      </div>

      <span className="taskbar__spacer" />

      {session && (
        <span className="taskbar__user mono" title="Makine sahibi">
          👤 {ownerName}
        </span>
      )}

      <button type="button" className="taskbar__icon" onClick={onLock} title="Kilitle">
        🔒
      </button>

      <button
        type="button"
        className="taskbar__icon"
        onClick={onNewNote}
        title="Yeni yapışkan not"
      >
        🗒️
      </button>

      <span className="taskbar__badge mono" title={`Tema: ${theme}`}>
        {productName}
      </span>

      <span className="taskbar__clock">
        {now.toLocaleDateString("tr-TR")} · {now.toLocaleTimeString("tr-TR")}
      </span>

      <ShutdownButton onShutdown={onLogout} label="Kapat" />
    </footer>
  );
}
